import React, { useState, useEffect } from 'react';
import { useAuthContext } from '../contexts/AuthContext.jsx';
import ConsumerProducts from './products/ConsumerProducts.jsx';
import Shippers from './products/Shippers.jsx';
import '../App.css';
import { toDisplayConsumerIdentifier } from '../libs/gtinUtils.js';

  // Helper function to transform a single product from API format to component format
  const transformProduct = (product) => ({
    ...product,
  gtin: toDisplayConsumerIdentifier(product.ean_upc || ''),
  });

export default function Products() {
  const { currentOrganization, canEditProducts, currentOrg, organization, products, refreshProducts, loading } = useAuthContext();
  const [activeTab, setActiveTab] = useState('consumer');
  const [searchTerm, setSearchTerm] = useState('');

  const rawConsumerProducts = products?.consumer_products ?? [];
  const consumerProducts = rawConsumerProducts.map(transformProduct);
  const rawShippers = products?.shippers ?? [];

  // Helper function to transform a single shipper from API format to component format
  const transformShipper = (shipper) => {
    // Find consumer products for this shipper


    const consumerProductIds = Object.keys(shipper.consumer_products);
    const shipperConsumerProducts = consumerProductIds.map(productId => {
      const product = consumerProducts.find(p => p.id === productId);
      return {
        ...product,
        quantity: shipper.consumer_products[productId]
      };
    });
    
    // Calculate total units
    const totalUnits = consumerProductIds.reduce((sum, productId) => sum + (shipper.consumer_products[productId] || 0), 0);
    
    return {
      id: shipper.id,
      name: shipper.name,
      gtin: shipper.gtin,
      is_active: shipper.is_active,
      total_units: totalUnits,
      consumer_products: shipperConsumerProducts
    };
  };

  const shippers = rawShippers?.map(transformShipper);

  // Filter products based on search term
  const filteredConsumerProducts = consumerProducts?.filter(product => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      product.product_name?.toLowerCase().includes(searchLower) ||
      product.ean_upc?.includes(searchTerm) ||
      product.ean_upc?.includes(searchTerm) ||
      product.product_line?.toLowerCase().includes(searchLower) ||
      product.customer_sku?.toLowerCase().includes(searchLower)
    );
  });

  // Filter shippers based on search term
  const filteredShippers = shippers?.filter(shipper => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      shipper.name?.toLowerCase().includes(searchLower) ||
      shipper.gtin?.includes(searchTerm)
    );
  });

  // Auto-switch to consumer products tab if no products exist and user is on shippers tab
  useEffect(() => {
    if (consumerProducts?.length === 0 && activeTab === 'shippers') {
      setActiveTab('consumer');
    }
  }, [consumerProducts?.length, activeTab]);




  // Background refresh function that doesn't show loading state
  const refreshDataInBackground = async () => {
    return await refreshProducts();
  };

  if (loading) {
    return (
      <div className="py-10">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="py-8">
          <h1 className="text-3xl font-bold text-gray-900">Products</h1>
          <p className="mt-2 text-sm text-gray-700">
            View & Manage {organization?.name || currentOrg?.name || 'your organization'}'s Consumer Products, Casepacks, and Shippers
          </p>
          
          {/* Tab Navigation */}
          <div className="mt-8">
            <nav className="flex space-x-8" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('consumer')}
                disabled={false}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'consumer'
                    ? 'border-primaryBrandColor text-primaryBrandColor'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Consumer Products ({consumerProducts.length})
              </button>
              <button
                onClick={() => consumerProducts.length > 0 && setActiveTab('shippers')}
                disabled={consumerProducts?.length === 0}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'shippers'
                    ? 'border-primaryBrandColor text-primaryBrandColor'
                    : consumerProducts.length === 0
                    ? 'border-transparent text-gray-400 cursor-not-allowed'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                title={consumerProducts.length === 0 ? 'Requires products first' : ''}
              >
                Shippers ({shippers.length})
              </button>
            </nav>
          </div>

          {/* Search Bar - Moved below tabs */}
          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                placeholder={activeTab === 'consumer' 
                  ? "Filter products by name, identifier, product line, or SKU..." 
                  : "Filter shippers by name or GTIN..."
                }
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primaryBrandColor focus:border-primaryBrandColor sm:text-sm"
              />
            </div>
          </div>

          {/* Tab Content */}
          <div className="mt-6">
            {activeTab === 'consumer' ? (
              <ConsumerProducts 
                products={filteredConsumerProducts}
                allProducts={consumerProducts}
                onRefresh={refreshDataInBackground}
                organizationId={currentOrganization}
                canEditProducts={canEditProducts}
              />
            ) : (
              <Shippers 
                shippers={filteredShippers} 
                consumerProducts={consumerProducts}
                onRefresh={refreshDataInBackground}
                organizationId={currentOrganization}
                canEditProducts={canEditProducts}
              />
            )}
          </div>

          {/* Help Button */}
          <div className="mt-8 text-right">
            <button
              onClick={() => {
                if (window.Intercom) {
                  window.Intercom('show');
                }
              }}
              className="text-sm text-primaryBrandColor hover:text-primaryBrandColor/80 underline cursor-pointer"
            >
              Need help updating products?
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
